/**
 * Page institutionnelle & technique :
 * Présentation du parcours d'homologation DMP / Certification CNDA pour les éditeurs de LPS.
 * Ton : neutre, factuel, conforme au guide SEL-MP-037 et au CDT DMP du GIE SESAM-Vitale.
 *
 * URL : /services/dmp/certification-cnda
 * Persona cible : éditeurs de logiciels santé, chefs de projet CNDA, intégrateurs DMP.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, FileCheck2, Workflow, BookOpen, AlertTriangle,
  KeyRound, FolderHeart, ListChecks, Network, Database, Layers,
  ArrowRight, Sparkles, Users, FileSignature, ScrollText, Lock,
  CheckCircle2,
} from "lucide-react";
import SEO, { ORG_SCHEMA, breadcrumb, faqSchema } from "../../components/SEO";

const CNDA_FAQ = [
  {
    q: "Qu'est-ce que la certification DMP / homologation CNDA ?",
    a: "L'homologation DMP, portée par le CNDA (Centre National de Dépôt et d'Agrément du GIE SESAM-Vitale), permet de démontrer qu'un logiciel de santé respecte les exigences techniques, fonctionnelles et réglementaires d'accès au Dossier Médical Partagé. Elle repose sur les transactions DMP décrites dans le guide d'intégration SEL-MP-037 et sur le Cahier de Tests (CDT DMP).",
  },
  {
    q: "Quelles sont les phases du parcours CNDA pour le DMP ?",
    a: "Quatre phases : (1) Prérequis (déclaration et vérification), (2) Tests fonctionnels (rejeu des cas du CDT), (3) Déclarations (comportements, périmètre, cinématiques), (4) Attestations et référencement. Chaque phase produit des preuves (logs, trames, captures) analysées par le CNDA.",
  },
  {
    q: "Quels profils DMP doivent être implémentés ?",
    a: "Cinq profils selon le périmètre : Accès sécurisé, Alimentation, Consultation, Web-PS contextuel et AIR (Authentification Indirecte Renforcée). Le périmètre dépend de l'usage cible du LPS (ville, hôpital, biologie, imagerie, secrétariat).",
  },
  {
    q: "TD0.2 est-il obligatoire avant chaque accès au DMP ?",
    a: "Oui. La transaction TD0.2 (test d'existence du DMP et vérification des droits) doit être réalisée avant toute alimentation ou consultation, même si le LPS conserve localement le statut du DMP ou des autorisations. Son omission est l'un des points de blocage les plus fréquents en homologation.",
  },
  {
    q: "Quels documents de référence faut-il connaître ?",
    a: "Principalement : SEL-MP-037 (guide d'intégration DMP), CDT DMP (cahier de tests), PDT-INF-526 (matrice des droits fonctionnels), PDT-INF-606 (matrice AIR), PDT-INF-527 (accès Web au système DMP), la matrice d'habilitations et les jeux de valeurs au format JSON.",
  },
  {
    q: "Quel est le rôle de MetaConnect dans l'homologation ?",
    a: "MetaConnect n'est pas l'organisme de validation : seul le CNDA valide. MetaConnect accompagne l'éditeur sur la compréhension du périmètre, l'intégration des transactions, la lecture des retours DMP, la préparation des preuves et l'analyse des écarts CNDA afin de réduire les risques de rejeu.",
  },
];

const CNDA_SCHEMA = [
  ORG_SCHEMA,
  breadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Services", url: "/solutions" },
    { name: "DMP", url: "/services/dmp" },
    { name: "Certification CNDA", url: "/services/dmp/certification-cnda" },
  ]),
  faqSchema(CNDA_FAQ),
  {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/mc2lqlf3_pdt.png",
    url: "https://metaconnect.fr/services/dmp/certification-cnda",
    name: "Certification DMP & homologation CNDA — infographie de synthèse",
    description: "Vue d'ensemble du parcours d'homologation DMP : acteurs, 4 phases CNDA, profils fonctionnels, transactions TD0.x à TD3.x, matrice des droits et documents de référence (SEL-MP-037, CDT, PDT-INF-526/606/527).",
    creator: { "@type": "Organization", name: "MetaConnect" },
    license: "https://metaconnect.fr/mentions-legales",
    acquireLicensePage: "https://metaconnect.fr/contact",
  },
  {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/lqvojot6_cnda%20pdt%202.png",
    url: "https://metaconnect.fr/services/dmp/certification-cnda",
    name: "Parcours d'accès au DMP — cinématique détaillée (modèle CNDR v1)",
    description: "Schéma complet du flux d'accès au DMP : sélection patient, qualification INS, TD0.1 / TD0.2, accès normal, alimentation TD2.x, modales de consentement A/B/C/D (bris de glace, non-opposition).",
    creator: { "@type": "Organization", name: "MetaConnect" },
    license: "https://metaconnect.fr/mentions-legales",
    acquireLicensePage: "https://metaconnect.fr/contact",
  },
  {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Certification DMP & homologation CNDA : parcours pour éditeurs de logiciels santé",
    description: "Présentation institutionnelle des étapes d'homologation DMP du CNDA : prérequis, tests fonctionnels, déclarations, attestations, profils DMP, transactions TD0.x à TD3.x.",
    image: [
      "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/mc2lqlf3_pdt.png",
      "https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/lqvojot6_cnda%20pdt%202.png",
    ],
    author: { "@type": "Organization", name: "MetaConnect", url: "https://metaconnect.fr" },
    publisher: {
      "@type": "Organization",
      name: "MetaConnect",
      logo: { "@type": "ImageObject", url: "https://metaconnect.fr/logos/metaconnect.jpeg" },
    },
    proficiencyLevel: "Expert",
    audience: { "@type": "BusinessAudience", audienceType: "Éditeurs de logiciels de santé (LPS)" },
    about: ["DMP", "CNDA", "GIE SESAM-Vitale", "INS", "FHIR", "Pro Santé Connect", "Référentiel ANS"],
    inLanguage: "fr-FR",
  },
];

export default function CertificationCnda() {
  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="page-cnda">
      <SEO
        title="Certification DMP et homologation CNDA · MetaConnect"
        description="L'homologation DMP du CNDA expliquée : prérequis, phases, profils, transactions TD0.x à TD3.x, matrice des droits, preuves attendues. Accompagnement éditeurs."
        canonical="/services/dmp/certification-cnda"
        image="https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/mc2lqlf3_pdt.png"
        schema={CNDA_SCHEMA}
      />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden hex-grid pt-32 pb-16 lg:pt-40 lg:pb-20" data-testid="cnda-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full"
               style={{ background: "radial-gradient(circle, rgba(45,123,255,0.20), transparent 60%)" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 glow-pill px-4 py-1.5 rounded-full text-[12px]">
            <Sparkles size={13} className="text-emerald-600" />
            Documentation institutionnelle &middot; Éditeurs de logiciels santé
          </div>
          <h1 className="font-display font-semibold text-[#0F2B5B] text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-[-0.02em] mt-6">
            Certification DMP &amp; <span className="bg-gradient-to-r from-[#7FB3FF] via-[#4F8FFF] to-[#2D7BFF] bg-clip-text text-transparent">homologation CNDA</span>
          </h1>
          <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[#475569] max-w-3xl mx-auto">
            MetaConnect accompagne les éditeurs de logiciels de santé dans l&apos;intégration du DMP
            et la préparation du référencement CNDA. Cette page présente les grandes étapes,
            les profils fonctionnels, les transactions <strong className="text-[#0F2B5B]">TD0.x à TD3.x</strong>{" "}
            et les principaux documents à connaître.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" className="btn-primary px-6 py-3 rounded-full text-[14px] font-medium inline-flex items-center gap-2" data-testid="cnda-cta-demo">
              Échanger sur votre projet d&apos;intégration <ArrowRight size={16} />
            </Link>
            <Link to="/services/dmp" className="btn-ghost px-6 py-3 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              Voir la brique MetaBridge DMP
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Infographie de synthèse — vue d'ensemble ===== */}
      <section className="py-16 lg:py-20" data-testid="cnda-infographic-overview">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <SectionEyebrow>Vue d&apos;ensemble</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight mt-3">
              Le parcours d&apos;homologation DMP en une infographie.
            </h2>
            <p className="mt-4 text-[14.5px] text-[#475569] max-w-3xl mx-auto leading-relaxed">
              Cette synthèse visuelle regroupe les acteurs, les 4 phases, les profils fonctionnels,
              les transactions TD, la matrice des droits et les documents de référence.
            </p>
          </div>
          <a href="https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/mc2lqlf3_pdt.png"
             target="_blank" rel="noopener noreferrer"
             className="block mc-card p-3 lg:p-4 overflow-hidden group hover:border-emerald-200 transition-colors duration-300"
             data-testid="cnda-infographic-link-1">
            <img
              src="https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/mc2lqlf3_pdt.png"
              alt="Infographie : Certification DMP & Homologation CNDA — vue synthétique des acteurs, phases, profils, transactions TD et documents de référence"
              loading="lazy"
              className="w-full h-auto rounded-lg bg-white"
            />
            <div className="mt-3 flex items-center justify-between gap-3 text-[12px]">
              <span className="text-[#475569]">Cliquez sur l&apos;image pour l&apos;ouvrir en taille réelle</span>
              <span className="text-[#0F2B5B] inline-flex items-center gap-1.5 group-hover:text-emerald-600">
                Ouvrir en grand <ArrowRight size={13} />
              </span>
            </div>
          </a>
        </div>
      </section>

      {/* ===== À qui s'adresse cette page ===== */}
      <section className="hex-soft py-16" data-testid="cnda-audience">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <SectionEyebrow>À qui s&apos;adresse cette présentation</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight mt-3">
            Une lecture technique pour les acteurs du logiciel santé.
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              "Éditeurs de logiciels de santé (LPS)",
              "Équipes produit",
              "Développeurs &amp; architectes",
              "Responsables conformité",
              "Chefs de projet CNDA",
              "Intégrateurs DMP / EAI",
              "Directions techniques",
              "DPO &amp; correspondants sécurité",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2.5 p-3.5 rounded-xl border border-slate-200 bg-[#F8FAFC]">
                <Users size={15} className="mt-0.5 text-[#0F2B5B] shrink-0" />
                <span className="text-[13.5px] text-[#334155]" dangerouslySetInnerHTML={{ __html: t }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Qu'est-ce que l'homologation DMP ===== */}
      <Section eyebrow="Définition" title="Qu'est-ce que l'homologation DMP ?" testId="cnda-definition">
        <p className="text-[15px] text-[#475569] leading-relaxed">
          L&apos;homologation ou le référencement DMP permet de démontrer qu&apos;un logiciel de santé
          est capable d&apos;accéder au système DMP conformément aux exigences du{" "}
          <strong className="text-[#0F2B5B]">GIE SESAM-Vitale / CNDA</strong>.
        </p>
        <p className="mt-4 text-[15px] text-[#475569] leading-relaxed">
          Elle vise à vérifier que le logiciel :
        </p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
          {[
            "s'authentifie correctement",
            "identifie correctement le patient (INS)",
            "contrôle l'existence du DMP",
            "vérifie les autorisations d'accès",
            "alimente le DMP avec des documents conformes",
            "recherche et consulte les documents",
            "respecte les règles de visibilité",
            "gère correctement les erreurs",
            "produit les preuves attendues",
          ].map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-[13.5px] text-[#334155]">
              <CheckCircle2 size={15} className="mt-0.5 text-emerald-600 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <Callout tone="info" icon={<BookOpen size={16} />}>
          Le guide d&apos;intégration DMP (SEL-MP-037) décrit les transactions intégrables dans les
          LPS, notamment les familles <strong>DMP_0.x</strong>, <strong>DMP_1.x</strong>,
          <strong> DMP_2.x</strong> et <strong>DMP_3.x</strong>.
        </Callout>
      </Section>

      {/* ===== Acteurs de la certification ===== */}
      <Section eyebrow="Écosystème" title="Les acteurs de la certification DMP" hexSoft testId="cnda-actors">
        <DataTable
          headers={["Acteur", "Rôle"]}
          rows={[
            ["Éditeur du LPS", "Développe et présente le logiciel candidat"],
            ["CNDA", "Analyse les preuves, les tests et les déclarations"],
            ["GIE SESAM-Vitale", "Porte les référentiels, guides et documents techniques"],
            ["ANS / Mon espace santé", "Écosystème national du DMP"],
            ["Connecteur / EAI", "Brique technique intégrée au LPS"],
            ["Professionnel de santé", "Utilisateur final du logiciel"],
            ["Patient", "Titulaire du DMP"],
          ]}
        />
      </Section>

      {/* ===== 4 Phases ===== */}
      <Section eyebrow="Parcours" title="Les 4 grandes phases du parcours CNDA" testId="cnda-phases">
        <div className="grid md:grid-cols-2 gap-4">
          <PhaseCard num="1" title="Prérequis"
            desc="Déclaration et vérification des prérequis du logiciel."
            items={[
              "Prérequis généraux", "Gestion des erreurs", "Synchronisation du temps",
              "Authentification directe / indirecte / AIR", "Accès Web-PS",
              "Alimentation &amp; consultation", "Gestion documentaire",
              "Gestion des modes d'accès",
            ]}
            footer="L'attestation de réalisation des prérequis CNDA liste les exigences à cocher selon le périmètre : prérequis communs, Web-PS, alimentation et consultation." />
          <PhaseCard num="2" title="Tests fonctionnels"
            desc="Rejouer les cas de test du CDT DMP."
            items={[
              "DMP inexistant / actif", "Autorisation absente / expirée",
              "Alimentation documentaire", "Recherche &amp; consultation",
              "Modification de visibilité", "Archivage / dépublication",
              "Accès Web-PS", "Authentification CPx / PSC / AIR",
            ]}
            footer="Chaque test produit des preuves : trames, logs, captures, codes retour, horodatage, patient de test." />
          <PhaseCard num="3" title="Déclarations"
            desc="Documenter le comportement du LPS."
            items={[
              "Fonctions implémentées / non implémentées",
              "Modes d'authentification supportés",
              "Comportements par défaut",
              "Paramètres disponibles",
              "Cas hors périmètre",
              "Cinématiques utilisateur",
            ]}
            footer="Les déclarations accompagnent les tests et précisent le périmètre couvert." />
          <PhaseCard num="4" title="Attestations &amp; référencement"
            desc="Documents administratifs liés au référencement."
            items={[
              "Attestation prérequis CNDA",
              "Attestation référencement DMP",
              "Identité du code source",
              "IHM fonctionnellement identique aux tests",
            ]}
            footer="L'attestation de référencement précise que les logiciels référencés doivent intégrer des codes sources identiques pour des fonctionnalités identiques et une IHM fonctionnellement identique au logiciel présenté aux tests." />
        </div>
      </Section>

      {/* ===== Profils DMP ===== */}
      <Section eyebrow="Profils fonctionnels" title="Les profils DMP concernés" hexSoft testId="cnda-profiles">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ProfileCard icon={<KeyRound size={20} />} title="Accès sécurisé" tone="mint"
            items={["Authentification", "Contrôle d'existence", "Autorisations", "Accès Web-PS"]} />
          <ProfileCard icon={<Layers size={20} />} title="Alimentation"
            items={["Création du document", "Métadonnées XDS", "Soumission au DMP", "Remplacement", "Dépôt par lot", "Gestion des visibilités"]} />
          <ProfileCard icon={<FolderHeart size={20} />} title="Consultation"
            items={["Recherche", "Consultation du contenu", "Documents archivés", "Documents masqués", "Invisibles patient", "Gestion des droits"]} />
          <ProfileCard icon={<Network size={20} />} title="Web-PS contextuel"
            items={["Passage de contexte utilisateur", "Passage de contexte patient", "Tableau de bord DMP", "Accès direct au DMP patient"]}
            note="PDT-INF-527" />
          <ProfileCard icon={<ShieldCheck size={20} />} title="AIR — Authentification Indirecte Renforcée" tone="mint"
            items={["Contrôles spécifiques DMP", "Matrice des droits AIR (PDT-INF-606)", "Cas usage déléguant / délégataire"]} />
          <ProfileCard icon={<FileSignature size={20} />} title="Identité patient"
            items={["INS qualifiée", "Identitovigilance", "Traits issus de l'INSi", "Distinction patient / représentant légal"]} />
        </div>
      </Section>

      {/* ===== Familles TD ===== */}
      <Section eyebrow="Familles de transactions" title="Les transactions TD du DMP" testId="cnda-td-families">
        <DataTable
          headers={["Famille", "Objet"]}
          rows={[
            ["TD0.x", "Accès sécurisé au DMP"],
            ["TD1.x", "Données administratives"],
            ["TD2.x", "Alimentation du DMP"],
            ["TD3.x", "Consultation et gestion documentaire"],
          ]}
        />

        <h3 className="font-display text-[#0F2B5B] text-xl sm:text-2xl font-semibold mt-12 mb-5">
          Détail des principales transactions
        </h3>
        <div className="grid gap-3">
          <TdItem code="TD0.1" title="Authentification au système DMP"
            desc="Permet d'accéder au système DMP de manière sécurisée."
            chips={["CPS", "CPF", "CPE", "Pro Santé Connect", "Authentification indirecte", "AIR"]} />
          <TdItem code="TD0.2" title="Test d'existence du DMP &amp; vérification des droits"
            desc="Transaction centrale du parcours. Vérifie l'existence du DMP, son statut actif, l'autorisation utilisateur et la possibilité d'accès."
            warning="Doit être réalisée AVANT toute alimentation ou consultation, même si le LPS conserve localement le statut du DMP ou des autorisations." />
          <TdItem code="TD0.3" title="Mise à jour de l'autorisation"
            desc="Ajout (TD0.3a) ou suppression (TD0.3b) d'une autorisation d'accès, incluant certains cas liés au médecin traitant."
            chips={["TD0.3a Ajout", "TD0.3b Suppression"]} />
          <TdItem code="TD0.4" title="Liste des DMP autorisés"
            desc="Récupère la liste des DMP pour lesquels l'utilisateur est autorisé." />
          <TdItem code="TD0.5" title="Recherche d'un DMP sans INS"
            desc="Encadrée et conditionnée par le mode d'accès, le rôle et le contexte." />
          <TdItem code="TD0.9" title="Accès Web-PS contextuel"
            desc="Ouvre le site Web-PS avec passage de contexte : tableau de bord DMP, accès direct au DMP patient, fonctions non disponibles en web-services." />
          <TdItem code="TD0.10" title="Web-PS contextuel en mode AIR"
            desc="Accès Web-PS dans un contexte d'authentification indirecte renforcée." />
          <TdItem code="TD1.3" title="Données administratives du DMP"
            desc="Consulte les données administratives et de gestion d'un DMP."
            warning="Certaines données retournées (ex. patientPerson) ne doivent pas être affichées par défaut. Seuls les traits d'identité de l'INS font foi." />
          <TdItem code="TD1.6" title="Liste des professionnels autorisés ou bloqués"
            desc="Connaît les professionnels autorisés, bloqués ou associés au dossier patient." />
          <TdItem code="TD2.1" title="Alimentation du DMP"
            desc="Alimente le DMP avec un ou plusieurs documents : construction, métadonnées XDS, CDA, signature, lot de soumission, remplacement."
            chips={["CDA R2", "Métadonnées XDS", "Lot de soumission", "Remplacement"]} />
          <TdItem code="TD2.2" title="Alimentation par CPE"
            desc="Couvre certains cas d'alimentation par carte CPE — contexte de secrétariat médical ou délégation." />
          <TdItem code="TD3.1" title="Recherche de documents"
            desc="Recherche dans le DMP par type, date, statut, archivage, identifiant technique."
            chips={["Sans critère", "Type", "Date", "Statut", "Archivés"]} />
          <TdItem code="TD3.2" title="Consultation de documents"
            desc="Récupère et consulte un document. Gère formats, droits, documents masqués, archivés, invisibles patient ou représentants légaux, restitution IHM." />
          <TdItem code="TD3.3" title="Gestion des attributs documentaires"
            desc="Cycle de vie du document : masquage / démasquage, visibilité patient, visibilité représentants légaux, suppression / dépublication, archivage."
            chips={["TD3.3a", "TD3.3b", "TD3.3c", "TD3.3d"]} />
        </div>
      </Section>

      {/* ===== Infographie parcours détaillé ===== */}
      <section className="hex-soft py-16 lg:py-20" data-testid="cnda-infographic-flow">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <SectionEyebrow>Cinématique d&apos;accès</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight mt-3">
              Parcours d&apos;accès au DMP — vue détaillée.
            </h2>
            <p className="mt-4 text-[14.5px] text-[#475569] max-w-3xl mx-auto leading-relaxed">
              Schéma complet du flux : sélection patient, qualification INS, TD0.1 / TD0.2,
              alimentation TD2.x, avec les modales A · B · C · D (consentement, bris de glace,
              non-opposition) et la correspondance transactions DMP.
            </p>
          </div>
          <a href="https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/lqvojot6_cnda%20pdt%202.png"
             target="_blank" rel="noopener noreferrer"
             className="block mc-card p-3 lg:p-4 overflow-hidden group hover:border-emerald-200 transition-colors duration-300"
             data-testid="cnda-infographic-link-2">
            <img
              src="https://customer-assets.emergentagent.com/job_digital-gateway-14/artifacts/lqvojot6_cnda%20pdt%202.png"
              alt="Schéma : Parcours d'accès au DMP — sélection patient, qualification INS, TD0.1, TD0.2, accès normal, alimentation, modales de consentement et bris de glace"
              loading="lazy"
              className="w-full h-auto rounded-lg bg-white"
            />
            <div className="mt-3 flex items-center justify-between gap-3 text-[12px]">
              <span className="text-[#475569]">
                Référence&nbsp;: modèle CNDR v1 &middot; cliquez sur l&apos;image pour l&apos;ouvrir en taille réelle
              </span>
              <span className="text-[#0F2B5B] inline-flex items-center gap-1.5 group-hover:text-emerald-600">
                Ouvrir en grand <ArrowRight size={13} />
              </span>
            </div>
          </a>
        </div>
      </section>

      {/* ===== Matrice des droits ===== */}
      <Section eyebrow="Habilitations" title="Matrice des droits fonctionnels" testId="cnda-matrix">
        <p className="text-[15px] text-[#475569] leading-relaxed">
          La matrice des droits indique quels profils peuvent réaliser quelles actions selon :
        </p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
          {[
            "Mode d'authentification", "Type de carte (CPS / CPF / CPE)",
            "Rôle du professionnel", "Mode d'accès",
            "Statut médecin traitant", "Contexte normal / régulation / bris de glace",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-[13.5px] text-[#334155]">
              <CheckCircle2 size={15} className="mt-0.5 text-emerald-600 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Callout tone="info" icon={<BookOpen size={16} />}>
          <strong>PDT-INF-526</strong> précise que les contrôles d&apos;autorisation sont effectués
          par le <em>système DMP</em>. La matrice est diffusée à titre informatif et n&apos;a pas
          vocation à être réimplémentée intégralement dans le LPS.
        </Callout>
      </Section>

      {/* ===== Identité patient & INS ===== */}
      <Section eyebrow="Identité" title="Identité patient et INS" testId="cnda-ins">
        <p className="text-[15px] text-[#475569] leading-relaxed">
          La certification DMP impose une attention particulière à l&apos;identité patient.
          Le LPS doit distinguer :
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {[
            { t: "Traits issus de l'INS", d: "Identité de référence (font foi)" },
            { t: "Traits retournés par certaines transactions", d: "Notamment patientPerson de TD0.2 / TD1.3" },
            { t: "Données locales du dossier patient", d: "Issues du SI métier" },
            { t: "Données issues du DMP", d: "Métadonnées documentaires" },
          ].map((x) => (
            <div key={x.t} className="mc-card p-4">
              <div className="font-display text-[#0F2B5B] text-[14.5px]">{x.t}</div>
              <div className="text-[13px] text-[#475569] mt-1">{x.d}</div>
            </div>
          ))}
        </div>
        <Callout tone="warning" icon={<AlertTriangle size={16} />}>
          Exigence fréquente : dans certains tests <strong>TD0.2</strong>, les traits d&apos;identité
          du bloc <code className="font-mono text-[12.5px] bg-black/30 px-1.5 py-0.5 rounded">patientPerson</code>{" "}
          retournés par la transaction <strong>ne doivent pas être affichés par défaut</strong>.
          Seuls les traits d&apos;identité de l&apos;INS font foi.
        </Callout>
      </Section>

      {/* ===== CDA & métadonnées ===== */}
      <Section eyebrow="Documents" title="CDA &amp; métadonnées XDS" hexSoft testId="cnda-cda">
        <p className="text-[15px] text-[#475569] leading-relaxed mb-5">
          L&apos;alimentation DMP repose sur la production de documents structurés et de
          métadonnées XDS conformes.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {[
            "CDA R2", "PDF/A", "Documents encapsulés", "Documents auto-présentables",
            "Identifiant unique", "classCode", "typeCode", "formatCode",
            "practiceSettingCode", "healthcareFacilityTypeCode", "confidentialityCode",
            "Auteur du document", "Patient", "Dates médicales", "Visibilité",
          ].map((c) => (
            <code key={c} className="font-mono text-[12px] px-3 py-2 rounded-lg border border-slate-200 bg-[#0F2B5B] text-white">
              {c}
            </code>
          ))}
        </div>
      </Section>

      {/* ===== Jeux de valeurs ===== */}
      <Section eyebrow="Référentiels" title="Jeux de valeurs structurés" testId="cnda-valuesets">
        <p className="text-[15px] text-[#475569] leading-relaxed">
          Le DMP utilise des jeux de valeurs structurés mis à jour régulièrement :
        </p>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {[
            "Professions", "Spécialités", "Rôles", "Genres d'activité",
            "Catégories de documents", "Formats", "Classes",
            "Cadres d'exercice", "Cadres de soin", "Modes d'authentification",
          ].map((v) => (
            <span key={v} className="text-[13px] px-3 py-2 rounded-full border border-slate-200 bg-[#EFF6FF] text-[#334155]">
              {v}
            </span>
          ))}
        </div>
        <Callout tone="info" icon={<Database size={16} />}>
          Les jeux de valeurs sont diffusés au format JSON. La fonction{" "}
          <code className="font-mono text-[12.5px] bg-black/30 px-1.5 py-0.5 rounded">getInteropCodes</code>{" "}
          permet de récupérer dynamiquement les jeux à jour.
        </Callout>
      </Section>

      {/* ===== Trames & preuves ===== */}
      <Section eyebrow="Preuves CNDA" title="Trames, logs et preuves" hexSoft testId="cnda-proofs">
        <p className="text-[15px] text-[#475569] leading-relaxed mb-5">
          Dans un parcours CNDA, les preuves sont essentielles. Chaque cas de test doit s&apos;accompagner
          des éléments suivants :
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { i: <ScrollText size={16} />, t: "Trames envoyées / reçues" },
            { i: <FileCheck2 size={16} />, t: "Captures écran" },
            { i: <Lock size={16} />, t: "Logs connecteur" },
            { i: <ListChecks size={16} />, t: "Réponses DMP + codes retour" },
            { i: <Workflow size={16} />, t: "Cinématique complète" },
            { i: <KeyRound size={16} />, t: "Mode d'authentification utilisé" },
            { i: <Users size={16} />, t: "Patient de test &amp; utilisateur" },
            { i: <BookOpen size={16} />, t: "Horodatage" },
          ].map((x) => (
            <div key={x.t} className="mc-card p-4 flex items-center gap-3">
              <div className="grid place-items-center h-9 w-9 rounded-lg bg-[#EFF6FF] border border-[#BAE6FD] text-[#0F2B5B] shrink-0">
                {x.i}
              </div>
              <span className="text-[13.5px] text-[#334155]" dangerouslySetInnerHTML={{ __html: x.t }} />
            </div>
          ))}
        </div>
      </Section>

      {/* ===== Erreurs fréquentes ===== */}
      <Section eyebrow="Points de vigilance" title="Erreurs fréquentes en homologation DMP" testId="cnda-pitfalls">
        <DataTable
          headers={["Sujet", "Risque"]}
          warning
          rows={[
            ["TD0.2 non systématique", "Blocage car contrôle obligatoire avant consultation/alimentation"],
            ["Autorisation expirée mal gérée", "Le LPS doit proposer une action cohérente"],
            ["Web-PS mal contextualisé", "Le CNDA peut refuser si le patient n'est pas ouvert directement"],
            ["patientPerson affiché par défaut", "Non-conformité aux exigences d'identité"],
            ["Mauvaise gestion majeur / mineur", "Visibilité représentants légaux incorrecte"],
            ["Messages d'irréversibilité absents", "Blocage sur TD3.3"],
            ["Métadonnées CDA incomplètes", "Rejet de l'alimentation"],
            ["Preuves incomplètes", "Rejeu demandé"],
            ["Boutons non autorisés visibles", "Risque IHM / conformité"],
            ["PSC ou CPx non justifié", "Commentaire attendu"],
          ]}
        />
      </Section>

      {/* ===== Rôle MetaConnect ===== */}
      <Section eyebrow="Accompagnement" title="Le rôle de MetaConnect dans l'homologation" hexSoft testId="cnda-mc-role">
        <p className="text-[15px] text-[#475569] leading-relaxed">
          MetaConnect accompagne les éditeurs dans la préparation de leur homologation DMP en
          facilitant :
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-2.5">
          {[
            "La compréhension du périmètre DMP",
            "L'intégration des transactions",
            "La structuration des cinématiques",
            "La lecture des retours DMP",
            "La gestion des cas d'erreur",
            "La préparation des preuves",
            "L'analyse des écarts CNDA",
            "La rédaction des commentaires techniques",
          ].map((s) => (
            <div key={s} className="flex items-start gap-2.5 p-3 rounded-lg border border-emerald-200 bg-emerald-50">
              <CheckCircle2 size={15} className="mt-0.5 text-emerald-600 shrink-0" />
              <span className="text-[13.5px] text-[#334155]">{s}</span>
            </div>
          ))}
        </div>
        <Callout tone="info" icon={<ShieldCheck size={16} />}>
          MetaConnect ne remplace pas la validation CNDA. Notre rôle est de{" "}
          <strong>sécuriser le parcours d&apos;intégration</strong> et de{" "}
          <strong>réduire les risques de rejeu</strong>.
        </Callout>
      </Section>

      {/* ===== Documents de référence ===== */}
      <Section eyebrow="Documentation" title="Documents de référence" testId="cnda-docs">
        <DataTable
          headers={["Document", "Rôle"]}
          rows={[
            ["SEL-MP-037", "Guide d'intégration du service DMP aux LPS"],
            ["CDT DMP", "Cahier de tests CNDA"],
            ["PDT-INF-526", "Matrice des droits fonctionnels DMP"],
            ["PDT-INF-606", "Matrice des droits fonctionnels AIR"],
            ["PDT-INF-527", "Accès Web au système DMP"],
            ["Matrice d'habilitations", "Droits de lecture par profession et type documentaire"],
            ["Jeux de valeurs", "Codes professions, documents, formats, spécialités"],
            ["Attestation prérequis", "Déclaration des prérequis CNDA"],
            ["Attestation référencement", "Référencement de logiciels utilisant le même code"],
          ]}
        />
      </Section>

      {/* ===== FAQ ===== */}
      <Section eyebrow="FAQ" title="Questions fréquentes" hexSoft testId="cnda-faq">
        <div className="grid gap-3">
          {CNDA_FAQ.map((q, i) => (
            <details key={i} className="mc-card p-5 group">
              <summary className="cursor-pointer font-display text-[#0F2B5B] text-[15px] flex items-center justify-between gap-3">
                {q.q} <ArrowRight size={14} className="text-[#0369A1] group-open:rotate-90 transition" />
              </summary>
              <p className="mt-3 text-[13.5px] text-[#475569] leading-relaxed">{q.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* ===== CTA ===== */}
      <section className="py-20 lg:py-24" data-testid="cnda-cta">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <ShieldCheck size={28} className="text-emerald-600 mx-auto mb-4" />
          <h2 className="font-display text-[#0F2B5B] text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight">
            Préparons ensemble votre parcours CNDA.
          </h2>
          <p className="mt-5 text-[15px] text-[#475569] max-w-xl mx-auto">
            Un expert MetaConnect cadre avec vous le périmètre DMP, identifie les transactions
            applicables et structure la production des preuves attendues par le CNDA.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" className="btn-primary px-6 py-3 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              Demander un échange technique <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="btn-ghost px-6 py-3 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ============================================================
   Sub-components (kept colocated to avoid premature abstraction)
   ============================================================ */

function SectionEyebrow({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.24em] text-[#0369A1]">
      {children}
    </div>
  );
}

function Section({ eyebrow, title, children, hexSoft = false, testId }) {
  return (
    <section className={`${hexSoft ? "hex-soft" : ""} py-16 lg:py-20`} data-testid={testId}>
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <SectionEyebrow>{eyebrow}</SectionEyebrow>
        <h2 className="font-display text-[#0F2B5B] text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight mt-3 mb-8"
            dangerouslySetInnerHTML={{ __html: title }} />
        {children}
      </div>
    </section>
  );
}

function Callout({ children, tone = "info", icon }) {
  const styles = tone === "warning"
    ? "border-red-300 bg-red-50 text-[#DC2626]"
    : "border-emerald-200 bg-emerald-50 text-emerald-600";
  return (
    <div className={`mt-6 rounded-xl border ${styles} px-5 py-4 flex items-start gap-3`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="text-[13.5px] leading-relaxed text-[#334155]">{children}</div>
    </div>
  );
}

function PhaseCard({ num, title, desc, items, footer }) {
  return (
    <div className="mc-card p-6 relative overflow-hidden">
      <div className="absolute top-3 right-4 font-display text-[64px] leading-none text-[#EFF6FF] font-bold pointer-events-none">
        {num}
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#0369A1]">Phase {num}</div>
      <h3 className="mt-2 font-display text-[#0F2B5B] text-[19px] leading-tight"
          dangerouslySetInnerHTML={{ __html: title }} />
      <p className="mt-2 text-[13px] text-[#475569]">{desc}</p>
      <ul className="mt-4 grid gap-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-[12.5px] text-[#475569]">
            <span className="mt-1.5 h-1 w-1 rounded-full bg-[var(--mc-mint)] shrink-0" />
            <span dangerouslySetInnerHTML={{ __html: it }} />
          </li>
        ))}
      </ul>
      {footer && (
        <p className="mt-4 pt-4 border-t border-slate-100 text-[11.5px] italic text-[#475569] leading-relaxed">
          {footer}
        </p>
      )}
    </div>
  );
}

function ProfileCard({ icon, title, items, tone = "blue", note }) {
  const ring = tone === "mint"
    ? "border-emerald-200 bg-emerald-50"
    : "";
  return (
    <div className={`mc-card p-5 ${ring}`}>
      <div className="flex items-center gap-2.5">
        <div className={`grid place-items-center h-9 w-9 rounded-lg ${
          tone === "mint"
            ? "bg-emerald-50 border border-emerald-200 text-emerald-600"
            : "bg-[#EFF6FF] border border-[#BAE6FD] text-[#0F2B5B]"
        }`}>{icon}</div>
        <h3 className="font-display text-[#0F2B5B] text-[15.5px] leading-tight">{title}</h3>
      </div>
      <ul className="mt-3 grid gap-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-[12.5px] text-[#475569]">
            <span className="mt-1.5 h-1 w-1 rounded-full bg-[var(--mc-blue-300)] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      {note && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-[#0369A1]">
          <BookOpen size={11} /> {note}
        </div>
      )}
    </div>
  );
}

function TdItem({ code, title, desc, chips = [], warning }) {
  return (
    <div className="mc-card p-5" data-testid={`td-${code.toLowerCase().replace(/\./g, "-")}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-[13px] px-2.5 py-0.5 rounded-md bg-[#EFF6FF] border border-[#BAE6FD] text-[#0F2B5B]">
          {code}
        </span>
        <h4 className="font-display text-[#0F2B5B] text-[15px] leading-snug"
            dangerouslySetInnerHTML={{ __html: title }} />
      </div>
      <p className="mt-2 text-[13.5px] text-[#475569] leading-relaxed">{desc}</p>
      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span key={c} className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 bg-[#0F2B5B] text-white">
              {c}
            </span>
          ))}
        </div>
      )}
      {warning && (
        <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 text-[#DC2626] shrink-0" />
          <span className="text-[12.5px] text-[#334155] leading-relaxed">{warning}</span>
        </div>
      )}
    </div>
  );
}

function DataTable({ headers, rows, warning = false }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-left text-[13.5px]">
        <thead className="bg-[#EFF6FF] text-[#0F2B5B] uppercase tracking-[0.18em] text-[11px]">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-4 py-3 font-semibold border-b border-slate-200">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-[#F8FAFC] transition-colors">
              {r.map((cell, j) => (
                <td key={j} className={`px-4 py-3 align-top ${
                  j === 0
                    ? "font-display text-[#0F2B5B] font-medium whitespace-nowrap"
                    : warning ? "text-[#DC2626]" : "text-[#475569]"
                }`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
